import { ReportInfoCard } from "st-app/api";
import { requireReportBrowser } from "./report-browser-context";
import { For, Show, Signal, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import './carousel-view.css'
import { debounce } from "common";
import SideNav from "./side-nav";
import STButton, { ButtonType } from "../st-button/st-button";
import ReportEditMenu from "./report-edit-menu";

export default function CarouselView() {
    const reportBrowser = requireReportBrowser()
    const [isZoomed, setIsZoomed] = createSignal(false)

    function onKeydown(e: KeyboardEvent) {
        switch (e.key) {
            case 'ArrowLeft':
                reportBrowser.carouselBackward()
                setIsZoomed(false)
                break;
            case 'ArrowRight':
                reportBrowser.carouselForward()
                setIsZoomed(false)
                break;
            case 'Escape':
                if (isZoomed()) {
                    setIsZoomed(false)
                } else {
                    reportBrowser.toggleCarousel()
                }
                break;
        }
    }

    function onWheel(e: WheelEvent) {
        if (!isZoomed()) {
            if (e.deltaY > 0) {
                reportBrowser.carouselForward()
            } else {
                reportBrowser.carouselBackward()
            }
        }
    }

    onMount(() => {
        document.addEventListener('keydown', onKeydown)
    })

    onCleanup(() => {
        document.removeEventListener('keydown', onKeydown)
    })

    return (
        <div style="display: flex; flex: 1; align-items: stretch; position: relative; height: 100%; gap: 4px;">
            <div style="position: relative; width: 100px; align-self: stretch;">
                <div style="position: absolute; height: 100%; overflow-y: auto;">
                    <SideNav></SideNav>
                </div>
            </div>
            <div class="carousel-nav-button" onClick={() => reportBrowser.carouselBackward()}>
                <span class="fa fa-chevron-left"></span>
            </div>

            <div class='carousel-cards' onWheel={onWheel}>
                <For each={reportBrowser.reports()}>{(report, i) => (
                    <ReportCarouselCard report={report} offset={i() - reportBrowser.state.carouselIndex} isZoomed={[isZoomed, setIsZoomed]}></ReportCarouselCard>
                )}</For>
            </div>
            <div class="carousel-nav-button" onClick={() => reportBrowser.carouselForward()}>
                <span class="fa fa-chevron-right"></span>
            </div>
        </div>
    )
}

const MIN_ZOOM = 1.5;
const MAX_ZOOM = 6;



function ReportCarouselCard(props: { report: ReportInfoCard, offset: number, isZoomed: Signal<boolean> }) {
    let img!: HTMLDivElement
    let preview!: HTMLDivElement

    const [isZoomed, setIsZoomed] = props.isZoomed
    const [origin, setOrigin] = createSignal('')
    const [previewLeft, setPreviewLeft] = createSignal('')
    const [previewTop, setPreviewTop] = createSignal('')
    const reportBrowser = requireReportBrowser()!
    const [zoomAmount, setZoomAmount] = createSignal(3)
    const [previewWidth, setPreviewWidth] = createSignal(0)
    const [previewHeight, setPreviewHeight] = createSignal(0)
    const [imageOffsetY, setImageOffsetY] = createSignal(0)
    const [containerWidth, setContainerWidth] = createSignal(0)
    const [containerHeight, setContainerHeight] = createSignal(0)

    const documentOnClick = () => {
        if (isZoomed()) {
            toggleZoom(false)
        }
    }

    const toggleZoom = debounce((z: boolean) => {
        if (z === true) {
            document.addEventListener('click', documentOnClick)
        } else {
            document.removeEventListener('click', documentOnClick)
        }
        setIsZoomed(z)
    }, 10)

    const launchReport = () => {
        reportBrowser.launchReportInterface(props.report)
    }

    onMount(() => {
        if (!img) return

        document.addEventListener('mousemove', (e) => {
            if (props.offset !== 0) return
            updateOrigin(e)
        })

        img.addEventListener('wheel', (ev) => {
            if (isZoomed() && props.offset === 0) {
                ev.preventDefault();
                ev.stopPropagation();
                if (ev.deltaY > 0) {
                    setZoomAmount(Math.max(MIN_ZOOM, zoomAmount() - 0.5))
                } else {
                    setZoomAmount(Math.min(MAX_ZOOM, zoomAmount() + 0.5))
                }
            }
        })
    })

    const onImgClick = (e: MouseEvent) => {
        if (e.defaultPrevented) return

        if (props.offset === 1) {
            reportBrowser.carouselForward()
        } else if (props.offset === -1) {
            reportBrowser.carouselBackward()
        } else if (props.offset === 0) {
            toggleZoom(!isZoomed())
            updateOrigin(e)
        }
    }

    onCleanup(() => {

    })

    const updateOrigin = (e: MouseEvent) => {
        if (!img) return
        const container = img.closest('.carousel-cards')
        if (!container) return

        const xPercent = (e.clientX - img.getBoundingClientRect().x) / img.clientWidth
        const yPercent = (e.clientY - img.getBoundingClientRect().y) / img.clientHeight

        const headerHeight = img.parentElement!.querySelector('.carousel-card-header')?.clientHeight ?? 68

        setContainerWidth(container.clientWidth)
        setContainerHeight(container.clientHeight)
        setPreviewWidth(containerWidth() * 0.9)
        setPreviewHeight(img.clientHeight);

        const iw = img.clientWidth
        const ih = img.clientHeight
        const pw = previewWidth()
        const ph = previewHeight()

        const scaledHeight = (img.clientHeight / img.clientWidth) * pw

        const leftOffset = Math.min(Math.max((xPercent * iw) - pw / 2, 0), iw - pw);
        const topOffset = Math.min(Math.max((yPercent * ih) - ph / 2, 0), ih - ph);

        const origin = `${xPercent * 100}% ${yPercent * 100}%`
        setOrigin(origin)

        const offsetY = Math.min(Math.max(scaledHeight * yPercent, 0), scaledHeight - ph)
        setImageOffsetY(offsetY)
        setPreviewLeft(`${leftOffset}px`)
        setPreviewTop(`${topOffset + headerHeight}px`)
    }

    const [isEditMenuOpen, setIsEditMenuOpen] = createSignal(false)

    return (
        <div
            class='carousel-card'
            classList={{
                'current': props.offset === 0,
                'left': props.offset === -1,
                'right': props.offset === 1,
                'outer-left': props.offset === -2,
                'outer-right': props.offset === 2,
                'hide': Math.abs(props.offset) > 2,
            }}
        >
            <div class='carousel-card-header'>
                <span class='report-number'>{props.report.number}</span>
                <span class='report-name'>{props.report.name}</span>
            </div>
            <div ref={img} class='carousel-image-container' onClick={onImgClick} data-active={isEditMenuOpen()}>
                <img
                    src={props.report.reportPreviewImage}
                    classList={{
                        'current-report': props.offset === 0,
                    }}
                ></img>

                <Show when={!isZoomed() && props.offset === 0}>
                    <div class='actions-overlay'>
                        <ReportEditMenu report={props.report} isOpen={[isEditMenuOpen, setIsEditMenuOpen]}></ReportEditMenu>
                    </div>
                    <div style='position: absolute; left: 0; top: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; pointer-events: none;'>
                        <STButton type="primary" onClick={launchReport}>
                            <div>
                                <span>Open Report</span>
                                <span class="fa fa-external-link" style="padding-left: 4px;"></span>
                            </div>
                        </STButton>
                    </div>
                </Show>
            </div>

            <Show when={isZoomed() && props.offset === 0}>
                <div
                    style={`position: absolute; width: ${containerWidth()}px; height: ${containerHeight()}px; background-color: black; opacity: 0.1; cursor: zoom-out;`}
                >
                </div>
            </Show>

            <div
                style={`position: absolute; height: ${previewHeight()}px; width: ${previewWidth()}px; top: ${previewTop()}; opacity: ${isZoomed() ? 100 : 0}%; pointer-events: none; overflow: hidden; z-index: 20; background-color: white; border-radius: 8px;`}
                ref={preview!}
            >
                <img
                    src={props.report.reportPreviewImage}
                    style={`width: ${previewWidth()}px; left: -${previewLeft()}; top: -${previewTop()}; transform: translateY(-${imageOffsetY()}px);`}
                ></img>
            </div>

        </div>
    )
}